<template>
  <d-card v-if="applicant">
    <v-form
      ref="form"
      :data="form"
      format="formData"
      :url="$laravel.route('public.applicant.update', {
        applicant: applicant.id,
      })"
      method="post"
      @success="onSuccess"
    >
      <template #default="{ errors }">
        <d-card-row>
          <v-input-select
            v-model="form.identity_type"
            name="identity_type"
            :label="$t('identity_type')"
            required
            @input="onIdentityTypeChange"
          >
            <option :value="null">
              {{ $t("select").capitalize() }}
            </option>
            <option
              v-for="(identity, key) in $laravel.applicantIdentityTypeEnum"
              :key="key"
              :value="identity.value"
            >
              {{ identity.label }}
            </option>
          </v-input-select>

          <d-notification
            v-if="notificationItems.length"
            type="is-info is-light"
          >
            <ul
              class="is-list is-list-disc"
            >
              <li
                v-for="(item, key) in notificationItems"
                :key="key"
              >
                <p>
                  {{ item }}
                </p>
              </li>
            </ul>
          </d-notification>
        </d-card-row>
        <d-card-row v-if="form.identity_type">
          <v-input-select
            v-if="form.identity_type === 'canadian_citizen'"
            v-model="form.identity_type_canadian_citizen"
            name="identity_type_canadian_citizen"
            :label="$t('identity_choice')"
            required
          >
            <option :value="null">
              {{ $t("select").capitalize() }}
            </option>
            <option
              v-for="(identityDocumentType, key) in identityCanadianCitizenDocumentTypes"
              :key="key"
              :value="identityDocumentType.value"
            >
              {{ identityDocumentType.label }}
            </option>
          </v-input-select>

          <v-input-select
            v-if="form.identity_type === 'permanent_resident'"
            v-model="form.identity_type_permanent_resident"
            name="identity_type_permanent_resident"
            :label="$t('identity_choice')"
            required
          >
            <option :value="null">
              {{ $t("select").capitalize() }}
            </option>
            <option
              v-for="(identityDocumentType, key) in identityPermanentResidentDocumentTypes"
              :key="key"
              :value="identityDocumentType.value"
            >
              {{ identityDocumentType.label }}
            </option>
          </v-input-select>
        </d-card-row>
        <d-card-row
          v-if="isShownPassportFile"
        >
          <v-input-media
            v-model="form.passport_file"
            name="passport_file"
            :label="$t('passport_file')"
            :existing-files="applicant.passport_file"
            :max="1"
            :required="isFieldPassportFileRequired"
          />
        </d-card-row>
        <d-card-row
          v-if="isShownDriverLicenseFile"
        >
          <v-input-media
            v-model="form.driving_license_file"
            name="driving_license_file"
            :existing-files="applicant.driving_license_file"
            :max="1"
            :label="$t('driving_license_file')"
            required
          />
        </d-card-row>
        <d-card-row
          v-if="isShownHealthInsuranceFile"
        >
          <v-input-media
            v-model="form.health_insurance_card_file"
            :existing-files="applicant.health_insurance_card_file"
            name="health_insurance_card_file"
            :max="1"
            :label="$t('health_insurance_card_file')"
            required
          />
        </d-card-row>
        <d-card-row
          v-if="isShownConfirmationPermanentResidenceFile"
        >
          <v-input-media
            v-model="form.confirmation_permanent_residence_file"
            :existing-files="applicant.confirmation_permanent_residence_file"
            name="confirmation_permanent_residence_file"
            :label="$t('confirmation_permanent_residence_file')"
            :max="1"
            required
          />
        </d-card-row>
        <d-card-row
          v-if="isShownResidencyCardFile"
        >
          <v-input-media
            v-model="form.residency_card"
            :existing-files="applicant.residency_card"
            name="residency_card"
            :label="$t('residency_card')"
            :max="1"
            required
          />
        </d-card-row>
        <d-card-row
          v-if="isShownWorkPermitFile"
        >
          <v-input-media
            v-model="form.work_permit_file"
            :existing-files="applicant.work_permit_file"
            name="work_permit_file"
            :label="$t('work_permit_file')"
            :max="1"
          />
        </d-card-row>
        <d-card-row
          v-if="isShownStudyPermitFile"
        >
          <v-input-media
            v-model="form.study_permit_file"
            :existing-files="applicant.study_permit_file"
            name="study_permit_file"
            :label="$t('study_permit_file')"
            :max="1"
          />
        </d-card-row>
        <d-card-row
          v-if="isShownCanadianAuthorizationAccessToTerritoryFile"
        >
          <v-input-media
            v-model="form.canadian_authorization_access_to_territory_file"
            :existing-files="applicant.canadian_authorization_access_to_territory_file"
            name="canadian_authorization_access_to_territory_file"
            :label="$t('canadian_authorization_access_to_territory_file')"
            :max="1"
          />
        </d-card-row>

        <d-card-row>
          <button
            class="button is-primary"
            type="submit"
          >
            {{ $t('submit_application') }}
          </button>
        </d-card-row>
      </template>
    </v-form>
  </d-card>
</template>

<script>
import { fillFields } from "@/services/helpers";
import { i18n } from "@/services/i18n";

export default {
  props: {
    applicant: {
      default: null,
      type: Object
    }
  },
  data () {
    return {
      canadianCitizenIdentityType: null,
      form: {
        identity_type: null,
        identity_type_canadian_citizen: null,
        identity_type_permanent_resident:null,
        passport_file: [],
        driving_license_file: [],
        health_insurance_card_file: [],
        work_permit_file: [],
        confirmation_permanent_residence_file: [],
        residency_card: [],
        study_permit_file: [],
        canadian_authorization_access_to_territory_file: [],
        step: "identity",
      }
    };
  },
  computed: {
    isShownPassportFile () {
      if (this.form.identity_type === "canadian_citizen") {
        return this.form.identity_type_canadian_citizen === "passport_file";
      }

      if (this.form.identity_type == "permanent_resident") {
        return this.form.identity_type_permanent_resident == "residency_card_not_received";
      }

      return this.form.identity_type ? true : false;
    },
    isShownDriverLicenseFile () {
      return this.form.identity_type === "canadian_citizen" && this.form.identity_type_canadian_citizen === "driving_license_file";
    },
    isShownHealthInsuranceFile () {
      return this.form.identity_type === "canadian_citizen" && this.form.identity_type_canadian_citizen === "health_insurance_card_file";
    },
    isShownConfirmationPermanentResidenceFile () {
      return this.form.identity_type === "permanent_resident" && this.form.identity_type_permanent_resident == "residency_card_not_received";
    },
    isShownResidencyCardFile() {
      return this.form.identity_type === "permanent_resident" && this.form.identity_type_permanent_resident == "resident_card_received";
    },
    isShownWorkPermitFile() {
      return this.form.identity_type === "foreign_worker";
    },
    isShownStudyPermitFile() {
      return this.form.identity_type === "foreign_student";
    },
    isShownCanadianAuthorizationAccessToTerritoryFile() {
      return this.form.identity_type === "other";
    },
    notificationItems() {
      if (this.form.identity_type === "canadian_citizen") {
        return [
          i18n.t("canadian_citizen_info_only_one_file_required"),
          i18n.t("canadian_citizen_info_identity_must_require_photo")
        ];
      }

      if (this.form.identity_type === "permanent_resident") {
        return [i18n.t("permanent_resident_identity_info")];
      }

      if (this.form.identity_type === "foreign_worker") {
        return [i18n.t("foreign_worker_identity_info")];
      }

      if (this.form.identity_type === "foreign_student") {
        return [i18n.t("foreign_student_identity_info")];
      }

      if (this.form.identity_type === "other") {
        return [i18n.t("other_identity_info")];
      }

      return [];
    },
    isFieldPassportFileRequired() {

      if (this.form.identity_type === "canadian_citizen") {
        return this.form.identity_type_canadian_citizen === "passport_file" ? true : false;
      }

      if  (this.form.identity_type === "permanent_resident") {
        return this.form.identity_type_permanent_resident == "residency_card_not_received";
      }

      if  (this.form.identity_type === "foreign_worker") {
        return false;
      }

      if  (this.form.identity_type === "foreign_student") {
        return false;
      }

      if  (this.form.identity_type === "other") {
        return false;
      }

      return false;
    },
    identityCanadianCitizenDocumentTypes()
    {
      return [
        {
          value: "passport_file",
          label: this.$t("passport_file")
        },
        {
          value: "driving_license_file",
          label: this.$t("driving_license_file")
        },
        {
          value: "health_insurance_card_file",
          label: this.$t("health_insurance_card_file")
        },
      ];
    },
    identityPermanentResidentDocumentTypes() {
      return [
        {
          value: "resident_card_received",
          label: this.$t("resident_card_received")
        },
        {
          value: "residency_card_not_received",
          label: this.$t("residency_card_not_received")
        },
      ];
    }
  },
  created () {
    // this.form = fillFields(this.form, this.applicant);
    this.form.identity_type = this.applicant.identity_type;
    this.form.identity_type_canadian_citizen = this.applicant.identity_type_canadian_citizen;
    this.form.identity_type_permanent_resident = this.applicant.identity_type_permanent_resident;
  },
  methods: {
    onSuccess() {
      this.$emit("success");
    },
    onIdentityTypeChange(value) {
      this.form.identity_type_canadian_citizen = null;
      this.form.identity_type_permanent_resident = null;
    }
  }
};
</script>


<style scoped>
.is-list-disc {
  list-style: disc;
  padding: 2px 15px;
}
.is-list-disc li {
  padding: 2px;
}
</style>
